import { onlineManager } from '@tanstack/react-query';

const STORAGE_KEY = 'lidea.manual_offline';

export function isManualOffline(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setManualOffline(manual: boolean): void {
  try {
    if (manual) {
      window.localStorage.setItem(STORAGE_KEY, '1');
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // ignore quota / private mode
  }
}

/** User explicitly chose hors-ligne — stay offline until they go back online. */
export function enterManualOffline(): void {
  setManualOffline(true);
  onlineManager.setOnline(false);
}

/** User explicitly chose en ligne — clear the lock and resume. */
export function exitManualOffline(): void {
  setManualOffline(false);
  onlineManager.setOnline(true);
}

/**
 * Reflect real network connectivity without overriding a voluntary HL mode.
 * Returns whether TanStack considers the app online after the update.
 */
export function applyNetworkConnectivity(connected: boolean): boolean {
  if (!connected) {
    onlineManager.setOnline(false);
    return false;
  }
  if (isManualOffline()) {
    onlineManager.setOnline(false);
    return false;
  }
  onlineManager.setOnline(true);
  return true;
}

/**
 * Replace TanStack's default window online/offline listener so a browser
 * "online" event cannot unlock a user-chosen HL mode.
 */
export function installManualOfflineAwareOnlineManager(): void {
  onlineManager.setEventListener((setOnline) => {
    const onOnline = () => {
      if (!isManualOffline()) {
        setOnline(true);
      }
    };
    const onOffline = () => setOnline(false);

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  });

  if (isManualOffline()) {
    onlineManager.setOnline(false);
  }
}
